import { getNode } from './navigation.js';
import { touchFilesystem } from './mutations.js';
import { folder } from './seed.js';

let _mountedDirs = [];

export function getMountedDirs() {
  return _mountedDirs;
}

export async function mountRealDirectory() {
  if (!globalThis.showDirectoryPicker) {
    alert('Your browser does not support the File System Access API. Try Chrome or Edge.');
    return null;
  }
  try {
    const handle = await globalThis.showDirectoryPicker({ mode: 'read' });
    const existing = _mountedDirs.find(d => d.handle === handle);
    if (existing) return existing.path;

    const name = handle.name;
    const path = `/Volumes/${name}`;
    const existingNode = getNode(path);
    if (existingNode) {
      existingNode.children = [];
      existingNode.handle = handle;
    } else {
      const volumes = getNode('/Volumes');
      if (!volumes) return null;
      const mountNode = folder(name, path, 'drive-removable');
      mountNode.handle = handle;
      mountNode.isMounted = true;
      volumes.children.push(mountNode);
    }

    _mountedDirs = [..._mountedDirs, { handle, path, name }];
    await refreshMountedDirectory(path);
    return path;
  } catch (err) {
    if (err.name !== 'AbortError' && err.name !== 'SecurityError') {
      console.warn('[mount] Directory selection failed:', err);
    }
    return null;
  }
}

export async function refreshMountedDirectory(path) {
  const node = getNode(path);
  if (!node?.handle) return;
  node.children = [];
  await readDirectoryIntoNode(node.handle, node);
  touchFilesystem();
}

function readMountedDirHandle(dirHandle) {
  const entries = [];
  return dirHandle.values().then(async (asyncIter) => {
    for await (const entry of asyncIter) {
      entries.push(entry);
    }
    entries.sort((a, b) => a.name.localeCompare(b.name));
    return entries;
  });
}

function mountNode(opts) {
  return {
    id: `mount-${opts.name}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    name: opts.name,
    path: opts.path,
    kind: opts.kind ?? 'file',
    icon: opts.icon ?? (opts.kind === 'folder' ? 'folder' : 'document'),
    children: opts.children ?? [],
    appId: opts.appId ?? null,
    modified: opts.modified ?? new Date().toISOString().slice(0, 10),
    size: opts.size ?? '--',
    content: opts.content ?? null,
    mimeType: opts.mimeType ?? null,
    originalPath: opts.originalPath ?? null,
    handle: null,
    fileHandle: null,
    isMounted: false,
  };
}

function getMimeType(name) {
  const ext = name.split('.').pop()?.toLowerCase();
  const map = {
    txt: 'text/plain', md: 'text/markdown', html: 'text/html', htm: 'text/html',
    css: 'text/css', js: 'text/javascript', json: 'application/json',
    xml: 'application/xml', csv: 'text/csv', rtf: 'application/rtf',
    pdf: 'application/pdf', png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg',
    gif: 'image/gif', svg: 'image/svg+xml', webp: 'image/webp',
    mp3: 'audio/mpeg', wav: 'audio/wav', ogg: 'audio/ogg',
    mp4: 'video/mp4', webm: 'video/webm',
  };
  return map[ext] ?? null;
}

export async function readDirectoryIntoNode(dirHandle, node) {
  try {
    const entries = await readMountedDirHandle(dirHandle);
    for (const entry of entries) {
      const childPath = node.path === '/' ? `/${entry.name}` : `${node.path}/${entry.name}`;
      if (entry.kind === 'directory') {
        const child = mountNode({
          name: entry.name, path: childPath, kind: 'folder', icon: 'folder',
        });
        child.handle = entry;
        node.children.push(child);
      } else {
        const child = mountNode({
          name: entry.name, path: childPath, kind: 'file',
          icon: getFileIcon(entry.name),
          mimeType: getMimeType(entry.name),
        });
        child.fileHandle = entry;
        node.children.push(child);
      }
    }
  } catch (err) {
    console.warn(`[mount] Error reading directory ${node.path}:`, err);
  }
}

function getFileIcon(name) {
  const ext = name.split('.').pop()?.toLowerCase();
  const icons = {
    pdf: 'pdf-document', png: 'image', jpg: 'image', jpeg: 'image',
    gif: 'image', svg: 'image', webp: 'image',
    mp3: 'audio', wav: 'audio', ogg: 'audio',
    mp4: 'video', webm: 'video',
    txt: 'document', md: 'document', html: 'document', htm: 'document',
    css: 'document', js: 'document', json: 'document',
    zip: 'archive', rar: 'archive', '7z': 'archive',
  };
  return icons[ext] ?? 'document';
}
